import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Stack,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Divider,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import { Add, Remove, Delete, Edit } from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../App";
import { updateOrder } from "../../service/orderService";
import AddCustomer from "../Customer/AddCustomer";
import { createCustomer, getAllCustomers } from "../../service/customerService";
import { getAllBranchLists } from "../../service/branchService";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Color from "../../components/Color";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const ORDER_TYPE = [
  { label: "Quotation", value: "quotation" },
  { label: "Invoice", value: "invoice" },
];

const DELIVERY_TYPE = [
  { label: "Home Delivery", value: "home_delivery" },
  { label: "Pickup", value: "pickup" },
];

const STATUS_OPTIONS = [
  { label: "Order Placed", value: "order_placed" },
  { label: "Ready for Delivery", value: "ready_for_delivery" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const paymentMethods = [
  { label: "Cash", value: "cash" },
  { label: "UPI", value: "upi" },
  { label: "Card", value: "card" },
  { label: "Bank Transfer", value: "bank_transfer" },
];

// Required fields
const requiredFields = [
  "name",
  "phone",
  "address",
  "contact_type",
  "order_date",
  "delivery_date",
  "order_type",
  "branch_id",
  "packing_charges",
  "salesman_name",
  "delivery_type",
  "customise_name",
  "cake_quantity",
];

const EditCakeDetails = () => {
  const [openAdvance, setOpenAdvance] = useState({});
  const [openOrder, setOpenOrder] = useState(false);
  const [openDelivery, setOpenDelivery] = useState(false);
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
  const [newPayment, setNewPayment] = useState({ amount: "", payment_method: "", payment_date: null });
  const [showAddPaymentFields, setShowAddPaymentFields] = useState(false);
  const iconRefs = useRef({});
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    gst_number: "",
    contact_type: "",
    order_date: null,
    delivery_date: null,
    delivery_address: "",
    payments: [{ amount: "", payment_method: "", payment_date: null }],
    pending: "0",
    order_type: "quotation",
    branch_id: "",
    status: "order_placed",
    branch_code: "",
    packing_charges: "0",
    salesman_name: "",
    discount_percentage: "0",
    delivery_type: "",
    invoice_number: "",
    notes: "",
    customise_name: "",
    cake_quantity: "1",
    discount_amount: "0.00",
    tax_amount: "0.00",
  });
  const [errors, setErrors] = useState({});
  const [sameAsAddress, setSameAsAddress] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [mode, setMode] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [totals, setTotals] = useState({ subtotal: 0, taxAmount: 0, total: 0 });
  const { showSnackbar } = useSnackbar();

  const roleId = localStorage.getItem("role") || "2";
  const branchId = localStorage.getItem("branch") || "";
  const branchName = branches.find((b) => b.id === Number(branchId))?.name || "Your Branch";

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllCustomers();
      const data = res?.data;
      if (Array.isArray(data?.data)) {
        setCustomers(data.data);
      } else {
        showSnackbar("Unexpected response format!", "error");
        setCustomers([]);
      }
    } catch (error) {
      showSnackbar("Failed to fetch customers!", "error");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllBranchLists();
      setBranches(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      showSnackbar("Failed to fetch branches!", "error");
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const handleBranchSelect = useCallback(
    (e) => {
      const { value } = e.target;
      setFormData((prev) => ({ ...prev, branch_id: value }));
      const selected = branches.find((b) => b.id === Number(value));
      if (selected) {
        setFormData((prev) => ({ ...prev, branch_code: selected.code }));
      }
      setErrors((prev) => ({
        ...prev,
        branch_id: value ? "" : "Branch is required",
      }));
    },
    [branches]
  );

  const handleCustomerSelect = (e) => {
    const value = e.target.value;
    if (value === "new") {
      setMode("new");
      setOpenModal(true);
      setSelectedCustomer(null);
      setFormData({
        ...formData,
        name: "",
        phone: "",
        email: "",
        gst_number: "",
        address: "",
        contact_type: "",
        delivery_address: "",
        payments: [{ amount: "", payment_method: "", payment_date: null }],
        branch_id: roleId === "2" ? branchId : formData.branch_id,
        branch_code:
          roleId === "2"
            ? branches.find((b) => b.id === Number(branchId))?.code || ""
            : formData.branch_code,
      });
      setSelectedCustomerId("");
      setErrors((prev) => ({ ...prev, customer_id: "Customer selection is required" }));
    } else {
      const selected = customers.find((c) => c.id === parseInt(value));
      setSelectedCustomerId(value);
      setSelectedCustomer(selected);
      setMode("existing");
      if (selected) {
        setFormData((prev) => ({
          ...prev,
          customer_id: value,
          name: selected.name || "",
          email: selected.email || "",
          phone: selected.phone || "",
          address: selected.address || "",
          gst_number: selected.gst_number || "",
          contact_type: selected.contact_type || "",
          delivery_address: sameAsAddress ? selected.address || "" : prev.delivery_address,
          branch_id: roleId === "2" ? branchId : prev.branch_id,
          branch_code:
            roleId === "2"
              ? branches.find((b) => b.id === Number(branchId))?.code || ""
              : prev.branch_code,
        }));
        setErrors((prev) => ({ ...prev, customer_id: "" }));
      }
    }
  };

  const resetForm = useCallback(() => {
    setOpenModal(false);
  }, []);

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    console.log("Initializing form with location.state:", location.state);
    console.log("editOrderCake from localStorage:", localStorage.getItem("editOrderCake"));
    const navCartItems = location.state?.cartItems || [];
    const storedOrder = JSON.parse(localStorage.getItem("editOrderCake")) || {};

    // Validate storedOrder
    if (
      !storedOrder ||
      !storedOrder.customer ||
      !storedOrder.items ||
      !Array.isArray(storedOrder.payments)
    ) {
      console.error("Invalid storedOrder data:", storedOrder);
      showSnackbar("Invalid order data. Please try again.", "error");
      setFormData((prev) => ({
        ...prev,
        payments: [{ amount: "", payment_method: "", payment_date: null }],
      }));
      return;
    }

    const customer = storedOrder.customer || {};
    const deliveryAddr = storedOrder.delivery_address || customer.address || "";
    const orderDate = storedOrder.order_date ? new Date(storedOrder.order_date) : null;
    const deliveryDate = storedOrder.delivery_date ? new Date(storedOrder.delivery_date) : null;
    const payments = Array.isArray(storedOrder.payments) && storedOrder.payments.length
      ? storedOrder.payments.map((p) => ({
          amount: p.amount?.toString() || "",
          payment_method: p.payment_method || "",
          payment_date: p.payment_date ? new Date(p.payment_date) : null,
        }))
      : [{ amount: "", payment_method: "", payment_date: null }];

    // Calculate pending amount safely
    const totalAmount = parseFloat(storedOrder.total_amount) || 0;
    const totalAdvance = Array.isArray(storedOrder.payments)
      ? storedOrder.payments.reduce((sum, p) => {
          const amount = parseFloat(p.amount);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0)
      : 0;
    const pending = (totalAmount - totalAdvance).toFixed(2);

    setFormData((prev) => ({
      ...prev,
      customer_id: customer.id?.toString() || "",
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      gst_number: customer.gst_number || "",
      contact_type: customer.contact_type || "",
      order_date: orderDate,
      delivery_date: deliveryDate,
      delivery_address: deliveryAddr,
      payments,
      pending: isNaN(pending) ? "0.00" : pending,
      order_type: storedOrder.order_type || "quotation",
      branch_id: roleId === "2" ? branchId : storedOrder.branch_id?.toString() || "",
      status: storedOrder.status || "order_placed",
      branch_code:
        roleId === "2"
          ? branches.find((b) => b.id === Number(branchId))?.code || ""
          : storedOrder.branch?.code || "",
      packing_charges: parseFloat(storedOrder.packing_charges || 0)
        .toFixed(2)
        .toString(),
      salesman_name: storedOrder.salesman_name || "",
      discount_percentage: parseFloat(storedOrder.discount_percentage || 0)
        .toFixed(2)
        .toString(),
      discount_amount: parseFloat(storedOrder.discount_amount || 0).toFixed(2).toString(),
      tax_amount: parseFloat(storedOrder.tax_amount || 0).toFixed(2),
      delivery_type: storedOrder.delivery_type || "",
      invoice_number: storedOrder.invoice_number || "",
      notes: storedOrder.notes || "",
      customise_name: storedOrder.items?.[0]?.customise_name || "",
      cake_quantity: storedOrder.items?.[0]?.cake_quantity?.toString() || "1",
    }));

    setSelectedCustomerId(customer.id?.toString() || "");
    setSelectedCustomer(customer);
    setMode("existing");
    setSameAsAddress(!deliveryAddr || deliveryAddr === customer.address);

    const orderItems = navCartItems.length > 0
      ? navCartItems
      : storedOrder.items?.map((item) => ({
          id: item.product.id,
          product_id: item.product.id,
          name: item.product.name,
          base_price: parseFloat(item.unit_price).toFixed(2),
          image_url: item.product.image_url,
          quantity: parseFloat(item.quantity),
          tax_rate: item.tax_rate || 0,
        })) || [];

    setCartItems(orderItems);
    localStorage.setItem("selectedCakes", JSON.stringify(orderItems));
  }, [location.state, roleId, branchId, branches, showSnackbar]);

  useEffect(() => {
    if (sameAsAddress) {
      setFormData((prev) => ({
        ...prev,
        delivery_address: prev.address || "",
      }));
    }
  }, [sameAsAddress, formData.address]);

  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      setTotals({ subtotal: 0, taxAmount: 0, total: 0 });
      setFormData((prev) => ({
        ...prev,
        pending: "0.00",
        discount_amount: "0.00",
      }));
      return;
    }

    const newTotals = cartItems.reduce(
      (acc, item) => {
        const price = parseFloat(item.base_price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const taxRate = parseFloat(item.tax_rate) || 0;
        const taxAmount = (price * taxRate) / 100;
        const unitPriceWithTax = price + taxAmount;
        acc.subtotal += price * quantity;
        acc.taxAmount += taxAmount * quantity;
        acc.total += unitPriceWithTax * quantity;
        return acc;
      },
      { subtotal: 0, taxAmount: 0, total: 0 }
    );

    const packingCharges = parseFloat(formData.packing_charges) || 0;
    const discountPercentage = parseFloat(formData.discount_percentage) || 0;
    const discountAmount = ((newTotals.subtotal + newTotals.taxAmount) * discountPercentage) / 100;
    newTotals.total = newTotals.subtotal + newTotals.taxAmount + packingCharges - discountAmount;

    const totalAdvance = formData.payments.reduce(
      (sum, payment) => sum + (parseFloat(payment.amount) || 0),
      0
    );
    const pending = newTotals.total - totalAdvance;

    setTotals({
      subtotal: newTotals.subtotal,
      taxAmount: newTotals.taxAmount,
      total: newTotals.total,
    });
    setFormData((prev) => ({
      ...prev,
      discount_amount: discountAmount.toFixed(2),
      pending: pending.toFixed(2),
    }));
  }, [formData.packing_charges, formData.payments, formData.discount_percentage, cartItems]);

  const handleChange = (e, index = null) => {
    const { name, value } = e.target;

    if (index !== null && name.startsWith("payments.")) {
      const field = name.split(".")[1];
      if (editingPaymentIndex === index && showAddPaymentFields) {
        setNewPayment((prev) => ({
          ...prev,
          [field]: value,
        }));

        if (field === "amount") {
          const numericValue = value === "" ? "" : Number(value);
          if (numericValue < 0) {
            setErrors((prev) => ({
              ...prev,
              [`newPayment.amount`]: "Advance amount cannot be negative",
            }));
          } else if (totals.total && numericValue > totals.total) {
            setErrors((prev) => ({
              ...prev,
              [`newPayment.amount`]: "Advance cannot be more than total amount",
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              [`newPayment.amount`]: "",
            }));
          }
        } else if (field === "payment_method") {
          if (!value || !paymentMethods.some((pm) => pm.value === value)) {
            setErrors((prev) => ({
              ...prev,
              [`newPayment.payment_method`]: "Please select a valid payment method",
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              [`newPayment.payment_method`]: "",
            }));
          }
        }
      } else {
        const updatedPayments = [...formData.payments];
        updatedPayments[index] = { ...updatedPayments[index], [field]: value };

        if (field === "amount") {
          const numericValue = value === "" ? "" : Number(value);
          if (numericValue < 0) {
            setErrors((prev) => ({
              ...prev,
              [`payments[${index}].amount`]: "Advance amount cannot be negative",
            }));
          } else if (totals.total && numericValue > totals.total) {
            setErrors((prev) => ({
              ...prev,
              [`payments[${index}].amount`]: "Advance cannot be more than total amount",
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              [`payments[${index}].amount`]: "",
            }));
          }
        } else if (field === "payment_method") {
          if (!value || !paymentMethods.some((pm) => pm.value === value)) {
            setErrors((prev) => ({
              ...prev,
              [`payments[${index}].payment_method`]: "Please select a valid payment method",
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              [`payments[${index}].payment_method`]: "",
            }));
          }
        }

        setFormData((prev) => ({ ...prev, payments: updatedPayments }));
      }
      return;
    }

    if (name === "discount_percentage" || name === "packing_charges" || name === "cake_quantity") {
      const numericValue = value === "" ? "" : Number(value);
      if (numericValue < 0) {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} cannot be negative`,
        }));
      } else if (name === "discount_percentage" && numericValue > 100) {
        setErrors((prev) => ({
          ...prev,
          discount_percentage: "Discount percentage cannot exceed 100%",
        }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }

    if (typeof value === "string" && value.trim() === "" && requiredFields.includes(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: `${name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} is required`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePaymentDateChange = (newValue, index = null) => {
    if (index !== null) {
      const updatedPayments = [...formData.payments];
      updatedPayments[index] = { ...updatedPayments[index], payment_date: newValue };

      if (!newValue) {
        setErrors((prev) => ({
          ...prev,
          [`payments[${index}].payment_date`]: "Payment date is required",
        }));
      } else if (formData.order_date && newValue < formData.order_date) {
        setErrors((prev) => ({
          ...prev,
          [`payments[${index}].payment_date`]: "Payment date must be on or after order date",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [`payments[${index}].payment_date`]: "",
        }));
      }
      setFormData((prev) => ({ ...prev, payments: updatedPayments }));
    } else {
      setNewPayment((prev) => ({ ...prev, payment_date: newValue }));
      if (!newValue) {
        setErrors((prev) => ({
          ...prev,
          [`newPayment.payment_date`]: "Payment date is required",
        }));
      } else if (formData.order_date && newValue < formData.order_date) {
        setErrors((prev) => ({
          ...prev,
          [`newPayment.payment_date`]: "Payment date must be on or after order date",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [`newPayment.payment_date`]: "",
        }));
      }
    }
  };

  const addPayment = () => {
    if (!newPayment.amount || !newPayment.payment_method || !newPayment.payment_date) {
      setErrors((prev) => ({
        ...prev,
        ...(newPayment.amount ? {} : { "newPayment.amount": "Advance amount is required" }),
        ...(newPayment.payment_method
          ? {}
          : { "newPayment.payment_method": "Payment method is required" }),
        ...(newPayment.payment_date ? {} : { "newPayment.payment_date": "Payment date is required" }),
      }));
      return;
    }

    const numericAmount = parseFloat(newPayment.amount);
    if (numericAmount <= 0) {
      setErrors((prev) => ({
        ...prev,
        "newPayment.amount": "Advance amount must be greater than 0",
      }));
      return;
    }
    if (totals.total && numericAmount > totals.total) {
      setErrors((prev) => ({
        ...prev,
        "newPayment.amount": "Advance cannot be more than total amount",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      payments: [...prev.payments, { ...newPayment }],
    }));
    setNewPayment({ amount: "", payment_method: "", payment_date: null });
    setShowAddPaymentFields(false);
    setErrors((prev) => ({
      ...prev,
      "newPayment.amount": "",
      "newPayment.payment_method": "",
      "newPayment.payment_date": "",
    }));
  };

  const editPayment = (index) => {
    setEditingPaymentIndex(index);
    setShowAddPaymentFields(true);
    setNewPayment({ ...formData.payments[index] });
  };

  const saveEditedPayment = () => {
    if (!newPayment.amount || !newPayment.payment_method || !newPayment.payment_date) {
      setErrors((prev) => ({
        ...prev,
        ...(newPayment.amount
          ? {}
          : { [`payments[${editingPaymentIndex}].amount`]: "Advance amount is required" }),
        ...(newPayment.payment_method
          ? {}
          : { [`payments[${editingPaymentIndex}].payment_method`]: "Payment method is required" }),
        ...(newPayment.payment_date
          ? {}
          : { [`payments[${editingPaymentIndex}].payment_date`]: "Payment date is required" }),
      }));
      return;
    }

    const numericAmount = parseFloat(newPayment.amount);
    if (numericAmount <= 0) {
      setErrors((prev) => ({
        ...prev,
        [`payments[${editingPaymentIndex}].amount`]: "Advance amount must be greater than 0",
      }));
      return;
    }
    if (totals.total && numericAmount > totals.total) {
      setErrors((prev) => ({
        ...prev,
        [`payments[${editingPaymentIndex}].amount`]: "Advance cannot be more than total amount",
      }));
      return;
    }

    const updatedPayments = [...formData.payments];
    updatedPayments[editingPaymentIndex] = { ...newPayment };
    setFormData((prev) => ({ ...prev, payments: updatedPayments }));
    setNewPayment({ amount: "", payment_method: "", payment_date: null });
    setEditingPaymentIndex(null);
    setShowAddPaymentFields(false);
    setErrors((prev) => ({
      ...prev,
      [`payments[${editingPaymentIndex}].amount`]: "",
      [`payments[${editingPaymentIndex}].payment_method`]: "",
      [`payments[${editingPaymentIndex}].payment_date`]: "",
    }));
  };

  const cancelEditPayment = () => {
    setNewPayment({ amount: "", payment_method: "", payment_date: null });
    setEditingPaymentIndex(null);
    setShowAddPaymentFields(false);
    setErrors((prev) => ({
      ...prev,
      "newPayment.amount": "",
      "newPayment.payment_method": "",
      "newPayment.payment_date": "",
    }));
  };

  const removePayment = (index) => {
    if (formData.payments.length === 1) {
      showSnackbar("At least one payment is required", "error");
      return;
    }
    const updatedPayments = formData.payments.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, payments: updatedPayments }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(prev).forEach((key) => {
        if (key.startsWith(`payments[${index}]`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const updateQuantity = (id, change) => {
    const updated = cartItems.map((item) =>
      item.id === id ? { ...item, quantity: Math.max(1, item.quantity + change) } : item
    );
    setCartItems(updated);
    localStorage.setItem("selectedCakes", JSON.stringify(updated));
  };

  const validate = () => {
    const newErrors = {};
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);

    requiredFields.forEach((field) => {
      const value = formData[field];
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "") ||
        (typeof value === "number" && isNaN(value))
      ) {
        newErrors[field] = `${field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
      }
    });

    if (!sameAsAddress && !formData.delivery_address.trim()) {
      newErrors.delivery_address = "Delivery address is required";
    }

    if (formData.email && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (
      formData.gst_number &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst_number)
    ) {
      newErrors.gst_number = "Invalid GST format";
    }
    if (!selectedCustomerId) {
      newErrors.customer_id = "Customer selection is required";
    }
    if (formData.order_date && formData.order_date > maxDate) {
      newErrors.order_date = "Order date cannot be more than one year in the future";
    }
    if (formData.delivery_date && formData.delivery_date < formData.order_date) {
      newErrors.delivery_date = "Delivery date must be on or after order date";
    }
    if (formData.delivery_date && formData.delivery_date > maxDate) {
      newErrors.delivery_date = "Delivery date cannot be more than one year in the future";
    }

    formData.payments.forEach((payment, index) => {
      if (payment.amount || payment.payment_method || payment.payment_date) {
        if (!payment.payment_date) {
          newErrors[`payments[${index}].payment_date`] = "Payment date is required";
        }
        if (!payment.payment_method || !paymentMethods.some((pm) => pm.value === payment.payment_method)) {
          newErrors[`payments[${index}].payment_method`] = "Please select a valid payment method";
        }
        const amount = parseFloat(payment.amount);
        if (!payment.amount || isNaN(amount) || amount <= 0) {
          newErrors[`payments[${index}].amount`] = "Advance amount must be greater than 0";
        } else if (totals.total && amount > totals.total) {
          newErrors[`payments[${index}].amount`] = "Advance cannot be more than total amount";
        }
      }
    });

    if (parseFloat(formData.packing_charges) < 0) {
      newErrors.packing_charges = "Packing charges cannot be negative";
    }
    if (formData.discount_percentage && isNaN(parseFloat(formData.discount_percentage))) {
      newErrors.discount_percentage = "Discount must be a valid number";
    }
    if (formData.discount_percentage && parseFloat(formData.discount_percentage) < 0) {
      newErrors.discount_percentage = "Discount percentage cannot be negative";
    }
    if (formData.discount_percentage && parseFloat(formData.discount_percentage) > 100) {
      newErrors.discount_percentage = "Discount percentage cannot exceed 100%";
    }
    if (cartItems.length === 0) {
      newErrors.cartItems = "At least one cake must be added to the order";
    }

    if (Object.keys(newErrors).length > 0) {
      showSnackbar("Please fix the following errors: " + Object.values(newErrors).join(", "), "error");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      requiredFields.every((field) => String(formData[field] ?? "").trim()) &&
      (sameAsAddress || formData.delivery_address.trim()) &&
      cartItems.length > 0 &&
      selectedCustomerId &&
      formData.payments.every(
        (payment) =>
          String(payment.amount ?? "").trim() &&
          payment.payment_method &&
          payment.payment_date
      )
    );
  };

  const handleCustomer = useCallback(
    async () => {
      try {
        const res = await createCustomer(formData);
        const newCustomer = res?.data?.data;
        showSnackbar("Customer added successfully!", "success");
        if (newCustomer?.id) {
          await fetchCustomers();
          setSelectedCustomerId(newCustomer.id.toString());
          setSelectedCustomer(newCustomer);
          setMode("existing");
          setFormData((prev) => ({
            ...prev,
            customer_id: newCustomer.id.toString(),
            name: newCustomer.name || "",
            email: newCustomer.email || "",
            phone: newCustomer.phone || "",
            address: newCustomer.address || "",
            gst_number: newCustomer.gst_number || "",
            contact_type: newCustomer.contact_type || "",
            delivery_address: sameAsAddress ? newCustomer.address || "" : prev.delivery_address,
            branch_id: roleId === "2" ? branchId : prev.branch_id,
            branch_code:
              roleId === "2"
                ? branches.find((b) => b.id === Number(branchId))?.code || ""
                : prev.branch_code,
            payments: prev.payments || [{ amount: "", payment_method: "", payment_date: null }],
          }));
          setErrors((prev) => ({
            ...prev,
            customer_id: "",
            name: "",
            email: "",
            phone: "",
            address: "",
            contact_type: "",
          }));
        }
        setOpenModal(false);
      } catch (error) {
        showSnackbar("Failed to add customer: " + (error.message || "Unknown error"), "error");
      }
    },
    [sameAsAddress, fetchCustomers, showSnackbar, roleId, branchId, branches]
  );

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    setSubmitting(true);

    const discountPercentage = parseFloat(formData.discount_percentage) || 0;
    const discountAmount = parseFloat(formData.discount_amount) || 0;

    const payload = {
      customer_id: selectedCustomerId,
      order_type: formData.order_type,
      branch_id: formData.branch_id,
      status: formData.status,
      order_date: formatDate(formData.order_date),
      delivery_date: formData.delivery_date ? formatDate(formData.delivery_date) : null,
      delivery_address: formData.delivery_address,
      payments: formData.payments
        .filter((payment) => payment.amount && parseFloat(payment.amount) > 0)
        .map((payment) => ({
          amount: parseFloat(payment.amount || 0).toFixed(2),
          payment_method: payment.payment_method,
          payment_date: payment.payment_date ? formatDate(payment.payment_date) : null,
        })),
      invoice_number: formData.invoice_number || "",
      branch_code: formData.branch_code,
      packing_charges: parseFloat(formData.packing_charges || 0).toFixed(2),
      salesman_name: formData.salesman_name,
      delivery_type: formData.delivery_type,
      notes: formData.notes,
      discount_percentage: Number(discountPercentage).toFixed(2),
      discount_amount: Number(discountAmount).toFixed(2),
      tax_amount: parseFloat(formData.tax_amount) || 0,
      customer: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        gst_number: formData.gst_number || "",
        contact_type: formData.contact_type,
      },
      items: cartItems.map((item) => ({
        product_id: item.product_id || item.id,
        quantity: item.quantity,
        price: parseFloat(item.base_price || 0).toFixed(2),
        is_cake: true,
        customise_name: formData.customise_name || "",
        cake_quantity: parseFloat(formData.cake_quantity || 1),
      })),
    };

    try {
      const res = await updateOrder(id, payload);
      const order = res?.data?.data;
      if (order) {
        localStorage.removeItem("editOrderCake");
        localStorage.removeItem("selectedCakes");
        localStorage.setItem("showCakeOrderSuccessToast", "1");
        navigate("/cake-order-confirm", { state: order });
      } else {
        showSnackbar("Invalid response from server", "error");
      }
    } catch (err) {
      console.error("Order submission failed:", err);
      const errorMessage = err.response?.data?.message || err.message || "Unknown error";
      showSnackbar(`Order submission failed: ${errorMessage}`, "error");
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        Object.values(apiErrors).forEach((messages) => {
          if (Array.isArray(messages)) {
            messages.forEach((msg) => showSnackbar(msg, "error"));
          } else {
            showSnackbar(messages, "error");
          }
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      mt={2}
      mb={{ xs: 8, sm: 6 }}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        p: { xs: 1, sm: 0 },
        gap: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 70%" },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 3 },
          minWidth: 0,
        }}
      >
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: "none" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 2,
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.1rem" },
            }}
          >
            Invoice Details
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Branch*
              </Typography>
              {roleId === "2" ? (
                <TextField
                  value={branchName}
                  placeholder="Branch"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    width: { xs: 280, sm: 300, md: 190, lg: 249 },
                    bgcolor: "#f1f1f1ee",
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0,
                  }}
                />
              ) : (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.branch_id}
                  sx={{
                    width: { xs: 280, sm: 300, md: 190, lg: 249 },
                    mb: 0,
                  }}
                  disabled={loading}
                >
                  <Select
                    name="branch_id"
                    value={formData.branch_id ?? ""}
                    onChange={handleBranchSelect}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Box sx={{ color: "text.secondary" }}>
                            Select branch
                          </Box>
                        );
                      }
                      return branches.find((b) => b.id === Number(value))?.name || "";
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select branch
                    </MenuItem>
                    {branches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.branch_id && <FormHelperText>{errors.branch_id}</FormHelperText>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Branch Code
              </Typography>
              <TextField
                value={formData.branch_code || ""}
                placeholder="Code"
                fullWidth
                InputProps={{ readOnly: true }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 200, lg: 150 },
                  bgcolor: "#f1f1f1ee",
                  "& .MuiInputLabel-root": { display: "none" },
                  mb: 0,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Invoice Number*
              </Typography>
              <TextField
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                placeholder="invoice number"
                fullWidth
                error={!!errors.invoice_number}
                helperText={errors.invoice_number}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  "& .MuiInputLabel-root": { display: "none" },
                  mb: 0,
                }}
                FormHelperTextProps={{
                  sx: { fontSize: "0.67rem", mt: 0.2 },
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: "#fff",
            boxShadow: "none",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 3,
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
            }}
          >
            Customer Details
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2 }} mb={2}>
            <Grid item xs={12}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Select Customer*
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.customer_id}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  mb: 0,
                }}
                disabled={loading}
              >
                <Select
                  value={selectedCustomerId ?? ""}
                  onChange={handleCustomerSelect}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Box sx={{ color: "text.secondary" }}>
                          Select customer
                        </Box>
                      );
                    }
                    if (value === "new") {
                      return "Add New Customer";
                    }
                    return customers.find((c) => c.id === parseInt(value))?.name || "";
                  }}
                >
                  <MenuItem value="" disabled>
                    Select customer
                  </MenuItem>
                  <Divider sx={{ border: "1px solid gray" }} />
                  {customers.map((cust) => (
                    <MenuItem key={cust.id} value={cust.id.toString()}>
                      {cust.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.customer_id && <FormHelperText>{errors.customer_id}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Status*
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.status}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  mb: 0,
                }}
              >
                <Select
                  name="status"
                  value={formData.status ?? ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Box sx={{ color: "text.secondary" }}>
                          Select Status
                        </Box>
                      );
                    }
                    return STATUS_OPTIONS.find((s) => s.value === value)?.label || "";
                  }}
                >
                  <MenuItem value="" disabled>
                    Select status
                  </MenuItem>
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
          {selectedCustomer && (
            <>
              <Typography
                sx={{ mb: 0, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Billing Address
              </Typography>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  mt: 1,
                  mb: 1,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "none",
                  border: "1px solid lightgray",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 600, mb: 0.5 }}
                    >
                      Name:
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 400,
                          ml: 1,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        }}
                      >
                        {selectedCustomer.name || "-"}
                      </Typography>
                    </Typography>
                    <Typography
                      sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 600, mb: 0.5 }}
                    >
                      Phone:
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 400,
                          ml: 1,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        }}
                      >
                        {selectedCustomer.phone || "-"}
                      </Typography>
                    </Typography>
                    <Typography
                      sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 600 }}
                    >
                      Address:
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 400,
                          ml: 1,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedCustomer.address || "-"}
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsAddress}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setSameAsAddress(isChecked);
                  if (isChecked) {
                    setFormData((prev) => ({
                      ...prev,
                      delivery_address: prev.address,
                    }));
                    setErrors((prev) => ({ ...prev, delivery_address: "" }));
                  }
                }}
                sx={{
                  color: Color.primary.main,
                  "&.Mui-checked": {
                    color: Color.primary.main,
                  },
                }}
              />
            }
            label="Same as billing address"
            sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}
          />
          {!sameAsAddress && (
            <Grid container spacing={2}>
              <Grid item xs={12} mt={1}>
                <Typography
                  sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
                >
                  Delivery Address*
                </Typography>
                <TextField
                  fullWidth
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  placeholder="Enter delivery address"
                  multiline
                  rows={3}
                  error={!!errors.delivery_address}
                  helperText={errors.delivery_address}
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    width: { xs: 280, sm: 250, md: 530, lg: 530 },
                    "& .MuiInputLabel-root": { display: "none" },
                    mb: 0,
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: "white",
            boxShadow: "none",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              mb: 3,
              fontWeight: 500,
            }}
          >
            Order Type
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }} mt={0}>
            <RadioGroup
              row
              name="order_type"
              value={formData.order_type}
              onChange={handleChange}
            >
              {ORDER_TYPE.map((type) => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": {
                          color: Color.primary.main,
                        },
                      }}
                    />
                  }
                  label={type.label}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Grid>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: "white",
            boxShadow: "none",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              mb: 3,
              fontWeight: 500,
            }}
          >
            Order Information
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2 }} mb={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Order Date*
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={formData.order_date}
                  open={openOrder}
                  onOpen={() => setOpenOrder(true)}
                  onClose={() => setOpenOrder(false)}
                  onChange={(newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      order_date: newValue,
                      delivery_date:
                        prev.delivery_date && newValue && prev.delivery_date < newValue
                          ? null
                          : prev.delivery_date,
                      payments: prev.payments.map((payment) => ({
                        ...payment,
                        payment_date:
                          payment.payment_date && newValue && payment.payment_date < newValue
                            ? null
                            : payment.payment_date,
                      })),
                    }));
                    if (!newValue) {
                      setErrors((prev) => ({
                        ...prev,
                        order_date: "Order date is required",
                      }));
                    } else {
                      const maxDate = new Date();
                      maxDate.setFullYear(maxDate.getFullYear() + 1);
                      if (newValue > maxDate) {
                        setErrors((prev) => ({
                          ...prev,
                          order_date: "Order date cannot be more than one year in the future",
                        }));
                      } else {
                        setErrors((prev) => ({ ...prev, order_date: "" }));
                        setOpenOrder(false);
                      }
                    }
                  }}
                  format="yyyy-MM-dd"
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.order_date,
                      helperText: errors.order_date,
                      onClick: () => setOpenOrder(true),
                      placeholder: "Select order date",
                      InputLabelProps: { shrink: false },
                      size: "small",
                      sx: {
                        width: { xs: 280, sm: 300, md: 190, lg: 249 },
                        "& .MuiInputLabel-root": { display: "none" },
                        mb: 0,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Delivery Date*
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={formData.delivery_date}
                  open={openDelivery}
                  onOpen={() => setOpenDelivery(true)}
                  onClose={() => setOpenDelivery(false)}
                  onChange={(newValue) => {
                    setFormData((prev) => ({ ...prev, delivery_date: newValue }));
                    if (!newValue) {
                      setErrors((prev) => ({
                        ...prev,
                        delivery_date: "Delivery date is required",
                      }));
                    } else {
                      const maxDate = new Date();
                      maxDate.setFullYear(maxDate.getFullYear() + 1);
                      const orderDate = formData.order_date || new Date();
                      orderDate.setHours(0, 0, 0, 0);
                      const newValueDate = new Date(newValue);
                      newValueDate.setHours(0, 0, 0, 0);
                      if (newValueDate < orderDate) {
                        setErrors((prev) => ({
                          ...prev,
                          delivery_date: "Delivery date must be on or after order date",
                        }));
                      } else if (newValueDate > maxDate) {
                        setErrors((prev) => ({
                          ...prev,
                          delivery_date: "Delivery date cannot be more than one year in the future",
                        }));
                      } else {
                        setErrors((prev) => ({ ...prev, delivery_date: "" }));
                        setOpenDelivery(false);
                      }
                    }
                  }}
                  format="yyyy-MM-dd"
                  minDate={formData.order_date || new Date()}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.delivery_date,
                      helperText: errors.delivery_date || "",
                      onClick: () => setOpenDelivery(true),
                      placeholder: "Select delivery date",
                      InputLabelProps: { shrink: false },
                      size: "small",
                      sx: {
                        width: { xs: 280, sm: 300, md: 190, lg: 249 },
                        "& .MuiInputLabel-root": { display: "none" },
                        mb: 0,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Salesman Name*
              </Typography>
              <TextField
                name="salesman_name"
                value={formData.salesman_name}
                onChange={handleChange}
                placeholder="Enter salesman name"
                fullWidth
                error={!!errors.salesman_name}
                helperText={errors.salesman_name}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  "& .MuiInputLabel-root": { display: "none" },
                  mb: 0,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Delivery Type*
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.delivery_type}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  mb: 0,
                }}
              >
                <Select
                  name="delivery_type"
                  value={formData.delivery_type ?? ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Box sx={{ color: "text.secondary" }}>
                          Select delivery type
                        </Box>
                      );
                    }
                    return DELIVERY_TYPE.find((s) => s.value === value)?.label || "";
                  }}
                >
                  <MenuItem value="" disabled>
                    Select delivery type
                  </MenuItem>
                  {DELIVERY_TYPE.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.delivery_type && <FormHelperText>{errors.delivery_type}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Customise Name*
              </Typography>
              <TextField
                name="customise_name"
                value={formData.customise_name}
                onChange={handleChange}
                placeholder="Enter customise name"
                fullWidth
                error={!!errors.customise_name}
                helperText={errors.customise_name}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  "& .MuiInputLabel-root": { display: "none" },
                  mb: 1,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Packing Charges*
              </Typography>
              <TextField
                name="packing_charges"
                type="number"
                value={formData.packing_charges}
                onChange={handleChange}
                placeholder="Enter packing charges"
                fullWidth
                error={!!errors.packing_charges}
                helperText={errors.packing_charges}
                inputProps={{ min: 0, step: "0.01" }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  "& .MuiInputLabel-root": { display: "none" },
                  mb: 0,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
              >
                Discount (%)*
              </Typography>
              <TextField
                name="discount_percentage"
                type="number"
                value={formData.discount_percentage}
                onChange={handleChange}
                placeholder="Enter discount percentage"
                fullWidth
                error={!!errors.discount_percentage}
                helperText={errors.discount_percentage}
                inputProps={{ min: 0, max: 100 }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  "& .MuiInputLabel-root": { display: "none" },
                  mb: 0,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography
              sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px", mt: 1 }}
            >
              Cake Instructions (optional)
            </Typography>
            <TextField
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Enter message for cake Customization, Date, etc"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              InputLabelProps={{ shrink: false }}
              sx={{
                width: { xs: 280, sm: 446, md: 600, lg: 515 },
                gridColumn: { sm: "span 2" },
                "& .MuiInputLabel-root": { display: "none" },
                mb: 0,
              }}
            />
          </Grid>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              mt: 5,
              mb: 3,
              fontWeight: 600,
            }}
          >
            Payment Information
          </Typography>
          <Card sx={{ mb: 2, boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)" }}>
            <CardContent sx={{ p: 0 }}>
              <Grid
                container
                gap={2}
                sx={{
                  backgroundColor: "#f5f5f5",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Grid
                  item
                  xs={3}
                  sx={{
                    minWidth: "120px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: Color.label.main,
                    }}
                  >
                    Payment Method
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{
                    minWidth: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: Color.label.main,
                    }}
                  >
                    Advance
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{
                    minWidth: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: Color.label.main,
                    }}
                  >
                    Advance Date
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{
                    minWidth: "80px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: Color.label.main,
                    }}
                  >
                    Actions
                  </Typography>
                </Grid>
              </Grid>
              {Array.isArray(formData.payments) && formData.payments.length > 0 ? (
                formData.payments.map((payment, index) => (
                  <Grid
                    container
                    gap={2}
                    key={index}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom:
                        index < formData.payments.length - 1 ? "1px solid #e0e0e0" : "none",
                      "&:hover": { backgroundColor: "#fafafa" },
                      alignItems: "center",
                    }}
                  >
                    <Grid
                      item
                      xs={3}
                      sx={{
                        minWidth: "120px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {paymentMethods.find((s) => s.value === payment.payment_method)?.label ||
                          "Unknown"}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={3}
                      sx={{
                        minWidth: "100px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        ₹{parseFloat(payment.amount).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={3}
                      sx={{
                        minWidth: "100px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {payment.payment_date
                          ? payment.payment_date.toLocaleDateString("en-GB")
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={3}
                      sx={{
                        minWidth: "80px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={() => editPayment(index)}
                        sx={{ color: "gray", p: 0.5 }}
                      >
                        <Edit sx={{ fontSize: "16px" }} />
                      </IconButton>
                      <IconButton
                        onClick={() => removePayment(index)}
                        sx={{ color: "#d32f2f", p: 0.5 }}
                      >
                        <Delete sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Grid>
                    {errors[`payments[${index}].amount`] && (
                      <Grid item xs={12}>
                        <FormHelperText error sx={{ textAlign: "center" }}>
                          {errors[`payments[${index}].amount`]}
                        </FormHelperText>
                      </Grid>
                    )}
                    {errors[`payments[${index}].payment_method`] && (
                      <Grid item xs={12}>
                        <FormHelperText error sx={{ textAlign: "center" }}>
                          {errors[`payments[${index}].payment_method`]}
                        </FormHelperText>
                      </Grid>
                    )}
                    {errors[`payments[${index}].payment_date`] && (
                      <Grid item xs={12}>
                        <FormHelperText error sx={{ textAlign: "center" }}>
                          {errors[`payments[${index}].payment_date`]}
                        </FormHelperText>
                      </Grid>
                    )}
                  </Grid>
                ))
              ) : (
                <Grid container sx={{ py: 2, textAlign: "center" }}>
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        color: "text.secondary",
                      }}
                    >
                      No advance payments added
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {showAddPaymentFields && (
                <Grid
                  container
                  sx={{
                    py: 1.5,
                    px: 1,
                    borderTop: formData.payments.length > 0 ? "1px solid #e0e0e0" : "none",
                    bgcolor: "#f9f9f9",
                  }}
                  gap={2}
                >
                  <Grid
                    item
                    xs={3}
                    sx={{
                      minWidth: "120px",
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <FormControl
                      fullWidth
                      size="small"
                      error={
                        !!errors[
                          editingPaymentIndex !== null
                            ? `payments[${editingPaymentIndex}].payment_method`
                            : "newPayment.payment_method"
                        ]
                      }
                    >
                      <Select
                        value={newPayment.payment_method}
                        onChange={(e) =>
                          setNewPayment((prev) => ({
                            ...prev,
                            payment_method: e.target.value,
                          }))
                        }
                        displayEmpty
                        renderValue={(value) => {
                          if (!value) {
                            return (
                              <Box sx={{ color: "text.secondary" }}>Payment</Box>
                            );
                          }
                          return (
                            paymentMethods.find((s) => s.value === value)?.label || ""
                          );
                        }}
                        sx={{ width: { xs: 260, sm: 150, md: 150, lg: 150 } }}
                      >
                        <MenuItem value="" disabled>
                          Payment method
                        </MenuItem>
                        {paymentMethods.map((s) => (
                          <MenuItem key={s.value} value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors[
                        editingPaymentIndex !== null
                          ? `payments[${editingPaymentIndex}].payment_method`
                          : "newPayment.payment_method"
                      ] && (
                        <FormHelperText>
                          {
                            errors[
                              editingPaymentIndex !== null
                                ? `payments[${editingPaymentIndex}].payment_method`
                                : "newPayment.payment_method"
                            ]
                          }
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      minWidth: "100px",
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <TextField
                      type="number"
                      value={newPayment.amount}
                      onChange={(e) =>
                        setNewPayment((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                      placeholder="Advance"
                      fullWidth
                      error={
                        !!errors[
                          editingPaymentIndex !== null
                            ? `payments[${editingPaymentIndex}].amount`
                            : "newPayment.amount"
                        ]
                      }
                      helperText={
                        errors[
                          editingPaymentIndex !== null
                            ? `payments[${editingPaymentIndex}].amount`
                            : "newPayment.amount"
                        ]
                      }
                      inputProps={{ min: 0 }}
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      sx={{
                        "& .MuiInputLabel-root": { display: "none" },
                        width: { xs: 260, sm: 150, md: 150, lg: 150 },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      minWidth: "100px",
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField
                          value={
                            newPayment.payment_date
                              ? newPayment.payment_date.toLocaleDateString("en-GB")
                              : "Date"
                          }
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setOpenAdvance((prev) => ({ ...prev, new: true }))
                                  }
                                  sx={{ p: 0.5 }}
                                  ref={(el) => (iconRefs.current.new = el)}
                                >
                                  <CalendarTodayIcon
                                    sx={{ fontSize: "22px", color: Color.primary.main }}
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          sx={{
                            "& .MuiInputLabel-root": { display: "none" },
                            width: { xs: 260, sm: 150, md: 150, lg: 150 },
                          }}
                          error={
                            !!errors[
                              editingPaymentIndex !== null
                                ? `payments[${editingPaymentIndex}].payment_date`
                                : "newPayment.payment_date"
                            ]
                          }
                          helperText={
                            errors[
                              editingPaymentIndex !== null
                                ? `payments[${editingPaymentIndex}].payment_date`
                                : "newPayment.payment_date"
                            ]
                          }
                        />
                        <DatePicker
                          open={openAdvance.new || false}
                          onClose={() =>
                            setOpenAdvance((prev) => ({ ...prev, new: false }))
                          }
                          value={newPayment.payment_date}
                          onChange={(newValue) =>
                            handlePaymentDateChange(newValue, editingPaymentIndex)
                          }
                          minDate={formData.order_date || null}
                          slots={{
                            textField: () => null,
                          }}
                          slotProps={{
                            popper: {
                              anchorEl: iconRefs.current.new,
                              placement: "bottom-start",
                            },
                          }}
                          enableAccessibleFieldDOMStructure={false}
                        />
                      </Stack>
                    </LocalizationProvider>
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      minWidth: "80px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={editingPaymentIndex !== null ? saveEditedPayment : addPayment}
                      sx={{
                        color: Color.primary.main,
                        border: `1px solid ${Color.primary.main}`,
                        "&:hover": {
                          color: Color.primary.dark,
                          borderColor: Color.primary.dark,
                        },
                        px: 2,
                        py: 0.5,
                        width: { xs: 200, sm: 50, md: 60, lg: 60 },
                      }}
                    >
                      {editingPaymentIndex !== null ? "Save" : "Add"}
                    </Button>
                    {editingPaymentIndex !== null && (
                      <Button
                        onClick={cancelEditPayment}
                        sx={{
                          color: "#d32f2f",
                          border: `1px solid #d32f2f`,
                          "&:hover": {
                            color: "#b71c1c",
                            borderColor: "#b71c1c",
                          },
                          px: 2,
                          py: 0.5,
                          width: { xs: 200, sm: 60, md: 50, lg: 50 },
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
         <Tooltip
            title={parseFloat(formData.pending) <= 0 ? "No pending amount to add advance" : ""}
            placement="top"
          >
            <span>
              <Button
                onClick={() => {
                  setShowAddPaymentFields(true);
                  setEditingPaymentIndex(null);
                  setNewPayment({ amount: "", payment_method: "", payment_date: null });
                }}
                startIcon={<Add />}
                disabled={parseFloat(formData.pending) <= 0}
                sx={{
                  mt: 1,
                  color: Color.primary.main,
                  border: `1px solid ${Color.primary.main}`,
                  "&:hover": { color: Color.primary.dark, borderColor: Color.primary.dark },
                  "&:disabled": {
                    color: "#7e7a7aff",
                    borderColor: "#7e7a7aff",
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                  },
                  px: 2,
                  py: 0.5,
                }}
              >
                Add Advance
              </Button>
            </span>
          </Tooltip>

          {/* Summary Section */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "#f9f9f9",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.03)",
              display: "flex",
              justifyContent: "flex-end",
              gap: 4,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: Color.label.main,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              Total: ₹{totals.total.toFixed(0)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: Color.label.main,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              Advance: ₹{formData.payments
                .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0)
                .toFixed(2)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: Color.label.main,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              Pending: ₹{formData.pending}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 30%" },
          position: { md: "sticky", xs: "static" },
          top: { md: 17 },
          alignSelf: { md: "flex-start" },
          height: { xs: "auto", sm: "70vh", md: "81vh" },
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 2,
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
            }}
          >
            Order Details
          </Typography>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: { xs: 0, sm: 1 },
              maxHeight: { xs: "auto", sm: "calc(70vh - 200px)", md: "calc(80vh - 200px)" },
            }}
          >
            {cartItems.map((item) => {
              const price = parseFloat(item.base_price || 0);
              // const total = price * item.quantity;
              //add                
             const gst = parseFloat(item.tax_rate || 0);
              const unitPriceWithTax = price + (price * gst) / 100;
              const taxAmount = (price * gst) / 100;
              const total = unitPriceWithTax * item.quantity;
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 2,
                    borderBottom: "1px solid #f0f0f0",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 120 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    >
                     ₹{unitPriceWithTax.toFixed(2)} × kg {item.quantity} (Tax: ₹{taxAmount.toFixed(2)})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    >
                      ₹ {total.toFixed(2)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => updateQuantity(item.id, -1)}
                        size="small"
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => updateQuantity(item.id, 1)}
                        size="small"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {cartItems.length === 0 && (
              <Typography
                variant="body2"
                color="error"
                textAlign="center"
                py={4}
                sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
              >
                No items in cart. Please specify cake details.
              </Typography>
            )}
          </Box>
         <Box mt={2} pt={2} borderTop="1px solid #eee">
  <Typography
    color="text.secondary"
    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
  >
    Subtotal: ₹{(totals.subtotal || 0).toFixed(2)}
  </Typography>
  <Typography
    color="text.secondary"
    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
  >
     Tax Amount: ₹{(parseFloat(formData.tax_amount) || 0).toFixed(2)}
  </Typography>
  <Typography
    color="text.secondary"
    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
  >
    Packing Charges: ₹{(parseFloat(formData.packing_charges) || 0).toFixed(2)}
  </Typography>
  <Typography
    color="text.secondary"
    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
  >
    Discount ({formData.discount_percentage || 0}%): ₹{(parseFloat(formData.discount_amount) || 0).toFixed(2)}
  </Typography>
  <Typography
    fontWeight={600}
    mt={1}
    sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" } }}
  >
    Total: ₹{(totals.total || 0).toFixed(0)}
  </Typography>
  <Box mt={3}>
    <Button
      fullWidth
      color="primary"
      onClick={handleSubmit}
      disabled={!isFormValid() || submitting}
      sx={{
        py: 1.3,
        borderRadius: 2,
        fontWeight: 600,
        background: Color.primary.main,
        color: "white",
        "&:disabled": {
          background: "#7e7a7aff",
          color: "#f3f0f0ff",
          cursor: "not-allowed",
          pointerEvents: "auto",
        },
        fontSize: { xs: "0.85rem", sm: "0.9rem" },
      }}
    >
      {submitting ? "Updating..." : "Update Order"}
    </Button>
    <Button
      fullWidth
      variant="outlined"
      onClick={() => navigate(-1)}
      sx={{
        mt: 2,
        py: 1.3,
        borderRadius: 2,
        borderColor: Color.secondary.main,
        color: Color.secondary.main,
        fontSize: { xs: "0.85rem", sm: "0.9rem" },
        "&:hover": {
          borderColor: Color.secondary.dark,
          color: Color.secondary.dark,
          backgroundColor: "transparent",
        },
      }}
    >
      Back to Product
    </Button>
  </Box>
</Box>
        </Paper>
      </Box>
      <AddCustomer
        open={openModal}
        onClose={resetForm}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleCustomer}
      />
    </Box>
  );
};

export default EditCakeDetails;